import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

interface VertexContextMenuProps {
  /** メニュー表示位置（クライアント座標。右クリック位置をそのまま渡す） */
  position: { x: number; y: number }
  /** 「削除」押下時のコールバック（呼び出し後は親が onClose する想定） */
  onDelete: () => void
  /** メニューを閉じる（外側クリック / Escape / 項目実行後） */
  onClose: () => void
}

/**
 * 頂点右クリックで表示する削除メニュー（Figma node-id=13797-2113 の注記）。
 *
 * ダークスタイルのメニュー 1 項目（削除 + ショートカット表記 "Delete"）。
 * ZoomableImage の CSS transform の影響を受けないよう body に portal し
 * クライアント座標へ fixed 配置する。
 */
export const VertexContextMenu: React.FC<VertexContextMenuProps> = ({
  position,
  onDelete,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null)
  // onClose はインライン関数で渡されるため、ref 経由で最新を参照して
  // リスナーの解除・再登録が毎レンダリング発生しないようにする (useCanvasToolMode と同じパターン)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) {
        return
      }
      onCloseRef.current()
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
      }
    }
    window.addEventListener('pointerdown', handlePointerDown, true)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return createPortal(
    <div
      className="fixed z-50 min-w-36 rounded-md bg-[#18181B] p-1 shadow-md"
      ref={menuRef}
      role="menu"
      style={{ left: position.x, top: position.y }}
    >
      <button
        // メニューを開いた直後に Enter / Space で「削除」を実行できるようにする (ARIA menu パターン)
        autoFocus
        className="flex w-full items-center justify-between gap-x-4 rounded px-2 py-1.5 text-left text-sm text-white transition-colors hover:bg-emerald-500 focus-visible:bg-emerald-500 focus-visible:outline-none"
        onClick={() => {
          onDelete()
          onClose()
        }}
        role="menuitem"
        type="button"
      >
        削除
        <span aria-hidden className="text-xs text-zinc-400">
          Delete
        </span>
      </button>
    </div>,
    document.body,
  )
}
